import {
  experimental_ComboboxMultipleField as ComboboxMultipleField,
  experimental_ComboboxMultiplePopover as ComboboxMultiplePopover,
  IconPlus,
  Text,
  experimental_useComboboxMultipleState as useComboboxMultipleState,
  useDebounce,
  useOnKeyDown,
} from '@vtex/admin-ui'
import React, { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { FieldDetails } from '../../FieldDetails'
import { messages } from './messages'
import styles from './multiInput.css'
import { MultiInputClientValidator, validationSuite } from './validation'

export interface MultiInputProps {
  name: string
  label: string
  values: string[]
  onChange: (values: string[]) => void
  inputValidation?: MultiInputClientValidator
  maxEntries?: number
  helpText?: string
  errorText?: string
  disabled?: boolean
  debounce?: number
}

function Option(props: { text: string }) {
  return (
    <FormattedMessage
      {...messages.option}
      values={{
        value: props.text,
        icon: <IconPlus className={styles.optionIcon} />,
        p: (content: React.ReactNode) => (
          <Text className={styles.optionText}>{content}</Text>
        ),
      }}
    />
  )
}

export function MultiInput(props: MultiInputProps) {
  const {
    name,
    label,
    values,
    onChange,
    inputValidation,
    helpText,
    errorText,
    disabled,
    maxEntries,
    debounce = 0,
  } = props

  const intl = useIntl()

  const suite = useMemo(
    () => validationSuite(name, intl, maxEntries, inputValidation),
    [name, intl, maxEntries, inputValidation]
  )

  const state = useComboboxMultipleState({
    defaultSelected: values,
    list: [],
    renderOption: (input) => {
      const validation = suite(input, values)

      if (!validation.isValid()) {
        return validation.errors[0].message || ''
      }

      return <Option text={input} />
    },
    renderTag: (text) => <span className={styles.tag}>{text}</span>,
    shouldClearOnSelect: true,
  })

  const hasMatches = state.matches.length > 0
  const [debouncedValue] = useDebounce(state.value, debounce)

  const popoverHidden =
    !hasMatches || (debounce > 0 && debouncedValue !== state.value)

  const updateStateFromTop = () => {
    state.setSelectedItems(values)
  }

  const updateStateFromUi = () => {
    if ((inputValidation && inputValidation(state.value)?.valid) ?? true) {
      onChange(state.selectedItems)
      return
    }
    updateStateFromTop()
  }

  const hasChanged = state.selectedItems !== values

  useEffect(() => {
    hasChanged && updateStateFromTop()
  }, [values])

  useEffect(() => {
    hasChanged && updateStateFromUi()
  }, [state.selectedItems])

  useEffect(() => {
    const matches = state.value.length ? [state.value] : []
    state.setMatches(matches)
  }, [state.value])

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain')

    if (!text.length) return

    const entries = text.split(/\n|\t/).map((entry) => entry.trim())
    const validEntries = entries.filter(
      (entry, index) =>
        entries.indexOf(entry) === index &&
        suite(entry, state.selectedItems).isValid()
    )

    const hasValidEntries = validEntries.length > 0
    const isWithinMaxEntries = !maxEntries || validEntries.length <= maxEntries
    if (hasValidEntries && isWithinMaxEntries) {
      state.setSelectedItems([...values, ...validEntries])
      e.preventDefault()
    }
  }

  const keyDownHandler = useOnKeyDown({
    keyMap: () => ({
      Enter: () =>
        suite(state.value, state.selectedItems).isValid() &&
        state.select(state.value),
    }),
  })

  return (
    <div>
      <ComboboxMultipleField
        id={name}
        data-testid={name}
        state={state}
        label={label}
        onKeyDown={keyDownHandler}
        onPaste={handlePaste}
        className={styles.field}
        data-error={errorText && errorText?.length !== 0}
        data-disabled={disabled}
        data-label={Boolean(label)}
      />
      <ComboboxMultiplePopover
        state={state}
        hidden={popoverHidden}
        className={styles.popover}
        data-disabled={!suite(state.value, state.selectedItems).isValid()}
      />
      <FieldDetails>
        <FieldDetails.Error text={errorText} />
        <FieldDetails.Line>
          <FieldDetails.Help text={helpText} />
          <FieldDetails.Counter count={values.length} max={maxEntries} />
        </FieldDetails.Line>
      </FieldDetails>
    </div>
  )
}
